import { nextTick, reactive, type Ref, type UnwrapNestedRefs } from 'vue';
import type { IExtremeMapRef } from 'src/models';
import type { MoviePlayerCompareModeState, SizeMode } from 'src/models/moviePlayer';

interface Deps {
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  refTopBarArea: Ref<HTMLDivElement | undefined>;
  refVideoArea: Ref<HTMLDivElement | undefined>;
  videoAspectRatio: number;
  isCurrentMovieListBothReady: () => boolean;
}

interface StyleState {
  videoAreaWidth: string;
  videoAreaHeight: string;
  infoHeight: string;
}

interface SizesState {
  control: SizeMode;
  meta: string;
}

interface MoviePlayerCompareModeLayoutContext {
  onResize: () => void;
  sizes: SizesState;
  styles: StyleState;
}

// useMoviePlayerCompareMode.tsから分割したリサイズ/レイアウト計算。
// window resizeイベント・動画切替後の再計算(onCurrentMovieListIdChange経由)の両方から
// オーケストレーター側がonResizeを呼び出す想定で、
// 呼び出し順序自体はオーケストレーター側が管理する。
export function useMoviePlayerCompareModeLayout(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): MoviePlayerCompareModeLayoutContext {
  const sizes = reactive<SizesState>({ control: 'md', meta: 'md' });
  const styles = reactive<StyleState>({
    videoAreaWidth: '50%',
    videoAreaHeight: '48%',
    infoHeight: '51%',
  });

  const resizeMap = () => {
    const mapHeight = parseInt(styles.infoHeight);
    if (!deps.refExtremeMap.value) return;

    deps.refExtremeMap.value.setMapHeight(mapHeight);
    deps.refExtremeMap.value.triggerResize();
  };
  const onResize = () => {
    if (!deps.isCurrentMovieListBothReady()) return;

    const windowW = window.innerWidth;
    const windowH = window.innerHeight;
    // モーダルのmarginおよびpadding, video間のmargin
    const wMargins = (8 * 2 + 8 * 2) + (2 * 6);
    const hMargins = 6 * 2 + 8 * 2;
    const videoW = (windowW - wMargins) / 3;
    const videoH = videoW / deps.videoAspectRatio;
    const videoWMargins = 8;
    styles.videoAreaWidth = parseInt((videoW * 2 + videoWMargins).toString()) + 'px';
    styles.videoAreaHeight = parseInt(videoH.toString()) + 'px';
    nextTick(() => {
      if (!deps.refTopBarArea.value || !deps.refVideoArea.value) return;

      const topBarH = deps.refTopBarArea.value.clientHeight;
      const videoAreaH = deps.refVideoArea.value.clientHeight;
      const otherHMargins = 16 + 8;
      const infoH = windowH - hMargins - topBarH - videoAreaH - otherHMargins;
      styles.infoHeight = infoH + 'px';
      resizeMap();
    });
    if (windowW >= 1400) {
      sizes.control = 'md';
      sizes.meta = 'md';
    } else if (windowW >= 1000) {
      sizes.control = 'sm';
      sizes.meta = 'sm';
    } else {
      sizes.control = 'xs';
      sizes.meta = 'xs';
    }
  };

  return { onResize, sizes, styles };
}
